import { useCallback, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';

/** Where Powens must send the browser back, on whichever origin we run on. */
export const POWENS_CALLBACK_PATH = '/accounts/powens/callback';

/**
 * Starts the Powens bank-linking flow.
 *
 * The webview URL is minted server-side (it needs the client secret and a
 * single-use code), so this only asks for it and hands the browser over.
 *
 * The current origin is sent along so a dev run returns to localhost instead of
 * production. The server only honours it if it is on its allow-list, so an
 * origin that is not declared fails loudly rather than silently redirecting
 * somewhere else.
 */
export function usePowensConnect() {
  const [isConnecting, setIsConnecting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const connect = useCallback(
    async (connectionId?: number) => {
      setIsConnecting(true);
      try {
        const { data, error } = await supabase.functions.invoke('powens-connect', {
          body: {
            redirect_uri: `${window.location.origin}${POWENS_CALLBACK_PATH}`,
            ...(connectionId ? { connection_id: connectionId } : {}),
          },
        });

        if (error) throw new Error(error.message);
        if (data?.error) throw new Error(data.error);
        if (!data?.url) throw new Error('No webview URL returned');

        // Full navigation, not a popup: the bank's own SCA screens routinely
        // break out of popups and iframes.
        window.location.href = data.url;
      } catch (err) {
        setIsConnecting(false);
        toast({
          title: t('powens.errorTitle'),
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
    },
    [toast, t]
  );

  return { connect, isConnecting };
}
